import { INVENTORY_CATEGORY_GROUPS } from "@/shared/constants/estateCategories";
import { formatDollars } from "@/shared/utils/formatters";
import {
  BRAND_GREEN,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  BORDER_COLOR,
  loadLogoAsDataUrl,
} from "@/shared/utils/pdfBranding";

import type { EstateItem, EstateSummary } from "@/shared/types/estateTypes";

interface GenerateInventoryPdfParams {
  assets: EstateItem[];
  debts: EstateItem[];
  summary: EstateSummary | undefined;
}

const MARGIN = 20;
const LINE_HEIGHT = 6;
const MARKED_AS_NONE = "__MARKED_AS_NONE__";

export async function generateInventoryPdf({ assets, debts, summary }: GenerateInventoryPdfParams) {
  const { default: jsPDF } = await import("jspdf");
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const contentWidth = pageWidth - 2 * MARGIN;
  let y = MARGIN;

  const checkPage = (needed: number) => {
    if (y + needed > pageHeight - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  };

  // --- Logo ---
  const logoDataUrl = await loadLogoAsDataUrl();
  const logoHeight = 8;
  const logoWidth = logoHeight * (1701 / 441);
  doc.addImage(logoDataUrl, "PNG", MARGIN, y, logoWidth, logoHeight);
  y += logoHeight + 8;

  // --- Divider ---
  doc.setDrawColor(...BORDER_COLOR);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 10;

  // --- Title ---
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...TEXT_PRIMARY);
  doc.text("Estate Inventory", MARGIN, y);
  y += 6;

  // --- Date ---
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...TEXT_SECONDARY);
  doc.text(
    `Generated ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`,
    MARGIN,
    y + 4,
  );
  y += 14;

  // --- Summary box ---
  const totalAssets = summary?.totalAssets ?? 0;
  const totalDebts = summary?.totalDebts ?? 0;
  const netValue = summary?.netValue ?? 0;

  doc.setDrawColor(...BORDER_COLOR);
  doc.setFillColor(250, 250, 249);
  doc.roundedRect(MARGIN, y, contentWidth, 28, 2, 2, "FD");

  const colWidth = contentWidth / 3;
  const summaryItems = [
    { label: "Total Assets", value: formatDollars({ amount: totalAssets, rounding: "whole" }) },
    { label: "Total Debts", value: formatDollars({ amount: totalDebts, rounding: "whole" }) },
    { label: "Net Estate Value", value: formatDollars({ amount: netValue, rounding: "whole" }) },
  ];

  summaryItems.forEach((item, i) => {
    const colX = MARGIN + colWidth * i + 8;
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...TEXT_SECONDARY);
    doc.text(item.label, colX, y + 10);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...TEXT_PRIMARY);
    doc.text(item.value, colX, y + 20);
  });

  y += 38;

  // --- Category sections ---
  const allItems = [...assets, ...debts];

  for (const group of INVENTORY_CATEGORY_GROUPS) {
    const groupItems = allItems.filter(
      (item) => group.subCategories.includes(item.category) && item.notes !== MARKED_AS_NONE,
    );
    const groupTotal = groupItems.reduce((sum, item) => sum + item.balance, 0);

    checkPage(20);

    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...TEXT_PRIMARY);
    doc.text(group.title, MARGIN, y);
    doc.setFont("helvetica", "normal");
    doc.text(formatDollars({ amount: groupTotal, rounding: "whole" }), pageWidth - MARGIN, y, {
      align: "right",
    });

    y += 3;
    doc.setDrawColor(...BRAND_GREEN);
    doc.setLineWidth(0.8);
    doc.line(MARGIN, y, pageWidth - MARGIN, y);
    y += 6;

    for (const subCategory of group.subCategories) {
      const subItems = groupItems.filter((item) => item.category === subCategory);

      checkPage(14);

      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...TEXT_SECONDARY);
      doc.text(subCategory, MARGIN + 2, y);
      y += LINE_HEIGHT;

      if (subItems.length === 0) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(...TEXT_SECONDARY);
        doc.text("None", MARGIN + 4, y);
        y += LINE_HEIGHT + 2;
      } else {
        for (const item of subItems) {
          checkPage(8);
          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(...TEXT_PRIMARY);
          doc.text(item.item_name, MARGIN + 4, y);
          doc.text(
            formatDollars({ amount: item.balance, rounding: "whole" }),
            pageWidth - MARGIN,
            y,
            {
              align: "right",
            },
          );
          y += LINE_HEIGHT;
        }
        y += 2;
      }
    }

    y += 4;
  }

  const footerY = pageHeight - 10;
  const totalPages = doc.getNumberOfPages();
  for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
    doc.setPage(pageNum);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...TEXT_SECONDARY);
    doc.text("Generated by Elayne - elayne.co", MARGIN, footerY);
    doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - MARGIN, footerY, { align: "right" });
  }

  doc.save("Estate_Inventory.pdf");
}
